import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Badge, IconButton, Popover, Tooltip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import EventBusyOutlinedIcon from "@material-ui/icons/EventBusyOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { useCompany } from "../../Context/CompanyContext";
import { fetchExpiryData } from "../Reports/reportsApi";
import useLiveData from "../../realtime/useLiveData";

const REFRESH_MS = 5 * 60 * 1000;

const useStyles = makeStyles({
  "@keyframes ring": {
    "0%, 100%": { transform: "rotate(0)" },
    "10%, 30%, 50%": { transform: "rotate(-14deg)" },
    "20%, 40%": { transform: "rotate(14deg)" },
    "60%": { transform: "rotate(0)" },
  },
  // On a coloured bar the default badge sits half outside the button and is clipped by the bar's top edge: keep it small, ring it in white, and tuck it into the icon's top-right corner without covering the bell.
  badgeOnDark: {
    minWidth: 18,
    height: 18,
    fontSize: "0.66rem",
    fontWeight: 800,
    padding: "0 4px",
    background: "#dc2626",
    color: "#fff",
    border: "1.5px solid #fff",
    boxSizing: "border-box",
    transform: "scale(1) translate(58%, -32%)",
  },
  ringing: { animation: "$ring 2.4s ease-in-out 1" },
  paper: { width: 340, maxWidth: "94vw", borderRadius: 14, overflow: "hidden", border: "1px solid #e6ebf2", boxShadow: "0 22px 44px -14px rgba(15,23,42,0.38)" },
  head: { padding: "10px 12px 8px", borderBottom: "1px solid #eef2f6", background: "linear-gradient(180deg,#fff7ed,#fff)" },
  title: { fontWeight: 800, color: "#0f172a", fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 6, lineHeight: 1.2 },
  tabs: { display: "flex", gap: 4, marginTop: 8 },
  tab: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    font: "inherit",
    fontSize: "0.72rem",
    fontWeight: 700,
    borderRadius: 9999,
    padding: "2px 10px",
    cursor: "pointer",
    transition: "background .12s, border-color .12s, color .12s",
    "&:hover": { borderColor: "#fdba74" },
  },
  tabOn: { background: "#fff7ed", borderColor: "#ef7f1a", color: "#c2410c" },
  // Slim scrollbar (WebKit + Firefox).
  list: {
    maxHeight: 300,
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#cbd5e1 transparent",
    "&::-webkit-scrollbar": { width: 5 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 9999 },
    "&::-webkit-scrollbar-thumb:hover": { background: "#94a3b8" },
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px 6px 9px",
    borderBottom: "1px solid #f1f5f9",
    borderLeft: "3px solid transparent",
    cursor: "pointer",
    transition: "background .1s",
    "&:hover": { background: "#fffaf5" },
    "&:focus-visible": { outline: "2px solid #ef7f1a", outlineOffset: -2 },
  },
  rowRed: { borderLeftColor: "#dc2626" },
  rowAmber: { borderLeftColor: "#f59e0b" },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: { fontWeight: 700, color: "#0f172a", fontSize: "0.82rem", lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  rowSub: { color: "#64748b", fontSize: "0.7rem", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  pill: { flex: "none", padding: "2px 8px", borderRadius: 9999, fontSize: "0.7rem", fontWeight: 800, whiteSpace: "nowrap" },
  empty: { padding: "20px 14px", textAlign: "center", color: "#475569" },
  foot: { padding: "6px 10px 8px", borderTop: "1px solid #eef2f6", background: "#fff" },
  footBtn: {
    width: "100%",
    border: 0,
    borderRadius: 9,
    background: "#fff7ed",
    color: "#c2410c",
    font: "inherit",
    fontSize: "0.78rem",
    fontWeight: 800,
    padding: "7px 10px",
    cursor: "pointer",
    transition: "background .12s",
    "&:hover": { background: "#ffedd5" },
  },
});

// "26 Sep" (the year only when it is not this year).
const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  const sameYear = d.getUTCFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }), timeZone: "UTC" });
};

const dayLabel = (row) =>
  row.daysLeft < 0 ? `${Math.abs(row.daysLeft)}d ago` : row.daysLeft === 0 ? "Today" : `${row.daysLeft}d left`;

// Bell for the top bar: how many batches are expired or close to it, and the
// soonest ones. Only exists while the SuperAdmin Batch & Expiry feature is on.
// Refreshes live when stock changes (and every few minutes as a backstop).
// `onDark`: for coloured top bars (POS) where the inherited colour is too dark to see.
const ExpiryBell = ({ color = "inherit", iconSize = 22, onDark = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const { company } = useCompany();
  // Feature on (SuperAdmin) and the store has not hidden the bell (Settings > Expiry).
  const enabled = company?.batchExpiryEnabled === true && company?.expiryBellEnabled !== false;
  const [data, setData] = useState({ summary: null, rows: [] });
  const [anchor, setAnchor] = useState(null);
  const [ring, setRing] = useState(false);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data: res } = await fetchExpiryData({ status: "attention", page: 1, limit: 8 });
      setData({ summary: res.summary || null, rows: res.rows || [] });
    } catch (error) {
      /* a failed refresh keeps showing the last numbers */
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData({ summary: null, rows: [] });
      return undefined;
    }
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  useLiveData(["stock", "items"], load, { minIntervalMs: 10000, enabled });

  const expired = data.summary?.expiredCount || 0;
  const near = data.summary?.nearCount || 0;
  const total = expired + near;

  // Ring the bell whenever the count goes up. (The once-a-day summary is the
  // pop-up in ExpiryAlertDialog.js, which the store can switch off separately.)
  const prevTotal = useRef(0);
  useEffect(() => {
    if (!enabled || !data.summary) return;
    if (total > prevTotal.current) {
      setRing(true);
      setTimeout(() => setRing(false), 2600);
    }
    prevTotal.current = total;
  }, [enabled, data.summary, total]);

  const visibleRows = data.rows.filter((r) => (tab === "expired" ? r.status === "Expired" : tab === "near" ? r.status !== "Expired" : true));

  if (!enabled) return null;

  const close = () => setAnchor(null);
  const goReport = () => {
    close();
    history.push("/reports/expiry");
  };

  return (
    <>
      <Tooltip title={total ? `${total} batch${total > 1 ? "es" : ""} need attention` : "No expiry alerts"}>
        <IconButton
          color={color}
          aria-label="Expiry alerts"
          onClick={(e) => setAnchor(e.currentTarget)}
          style={onDark ? { padding: 8, color: "#fff", background: "rgba(255,255,255,0.22)" } : { padding: 8 }}
        >
          <Badge
            badgeContent={total}
            max={99}
            color={expired ? "error" : "secondary"}
            overlap="rectangular"
            classes={onDark ? { badge: classes.badgeOnDark } : undefined}
          >
            {total ? (
              <NotificationsActiveIcon style={{ fontSize: iconSize }} className={ring ? classes.ringing : ""} />
            ) : (
              <NotificationsNoneIcon style={{ fontSize: iconSize }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.paper }}
        marginThreshold={8}
      >
        <div className={classes.head}>
          <Typography className={classes.title}>
            <EventBusyOutlinedIcon style={{ color: "#ea580c", fontSize: 18 }} /> Expiry alerts
          </Typography>
          <div className={classes.tabs} role="tablist" aria-label="Filter alerts">
            {[
              ["all", `All ${total}`],
              ["expired", `Expired ${expired}`],
              ["near", `Soon ${near}`],
            ].map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} className={`${classes.tab} ${tab === key ? classes.tabOn : ""}`} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {data.rows.length === 0 ? (
          <div className={classes.empty}>
            <CheckCircleOutlineIcon style={{ fontSize: 34, color: "#16a34a" }} />
            <Typography style={{ fontWeight: 700, marginTop: 4, fontSize: "0.9rem" }}>All good</Typography>
            <Typography variant="caption" color="textSecondary">Nothing is expired or expiring soon.</Typography>
          </div>
        ) : (
          <div className={classes.list}>
            {visibleRows.length === 0 && (
              <div className={classes.empty}>
                <Typography variant="caption" color="textSecondary">Nothing in this list.</Typography>
              </div>
            )}
            {visibleRows.map((row) => {
              const isExpired = row.status === "Expired";
              return (
                <div
                  key={`${row.itemId}-${row.batchNo}-${row.expiryDate}`}
                  className={`${classes.row} ${isExpired ? classes.rowRed : classes.rowAmber}`}
                  role="button"
                  tabIndex={0}
                  onClick={goReport}
                  onKeyDown={(e) => e.key === "Enter" && goReport()}
                >
                  <div className={classes.rowMain}>
                    <div className={classes.rowName} title={row.itemName}>{row.itemName}</div>
                    <div className={classes.rowSub} title={`Batch ${row.batchNo}`}>
                      {row.batchNo} · {row.qty} {row.unit} · {fmtDate(row.expiryDate)}
                    </div>
                  </div>
                  <span className={classes.pill} style={{ background: isExpired ? "#fee2e2" : "#fef3c7", color: isExpired ? "#b91c1c" : "#b45309" }}>
                    {dayLabel(row)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className={classes.foot}>
          <button type="button" className={classes.footBtn} onClick={goReport}>
            {total > data.rows.length ? `See all ${total} in the report →` : "Open the expiry report →"}
          </button>
        </div>
      </Popover>
    </>
  );
};

export default ExpiryBell;
